import {naturalSort, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {PlateMap, ReagentAssignmentGroup, Source} from 'samples/plate-maps/PlateMapConstants';
import {getGroupIdsForSource} from 'samples/plate-maps/PlateMapUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARK, RED_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Popover from 'ui-library/floating-ui/Popover';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

interface ClearWellsDropdownOptionContentProps {
  schemas: Array<ReagentAssignmentGroup>;
  numSources: number;
}

export function ClearWellsDropdownContent({
  schemas,
  numSources,
}: ClearWellsDropdownOptionContentProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.clientWidth < ref.current.scrollWidth) {
      setOverflowing(true);
    }
  }, [ref]);

  return (
    <div className={css(styles.clearReagentOption)}>
      {schemas.map((schema) => (
        <div key={schema.id} className={css(styles.swatch)} style={{backgroundColor: schema.color}} />
      ))}
      <div className={css(styles.schemasWrapper)}>
        <div className={css(styles.schemaNamesWrapper)}>
          <div className={css(styles.schemaNames)} ref={ref}>
            {schemas.map((schema) => schema.name).join('; ')}
          </div>
          {overflowing && (
            <Popover renderContent={() => schemas.map((schema) => <div key={schema.id}>{schema.name}</div>)}>
              <div className={css(styles.seeMore)}>See more</div>
            </Popover>
          )}
        </div>
        <div className={css(styles.sourcesCount)}>{`${numSources} ${pluralize(numSources, 'source')}`}</div>
      </div>
    </div>
  );
}

// Exported for testing.
export const CLEAR_ALL_SENTINEL = 'CLEAR_ALL_SENTINEL';

interface ClearWellsDropdownProps {
  /** The set of wells that have been selected in the plate map. */
  selectedWellIndices: Set<number>;
  /** The plate map to clear wells on */
  plateMap: PlateMap;
  /** A map of reagent group ids to reagent group */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** A function to remove all reagents containing exactly the specified reagent group ids */
  removeReagentGroups: (reagentGroupIds: Set<string>, clearAllWells: boolean) => void;
  /** A function to remove all reagent groups */
  removeAllReagentGroups: (clearAllWells: boolean) => void;
}

export default function ClearWellsDropdown({
  selectedWellIndices,
  plateMap,
  reagentGroupsById,
  removeReagentGroups,
  removeAllReagentGroups,
}: ClearWellsDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Get all distinct reagents in the selected wells.
  const clearAllWells = selectedWellIndices.size === 0;
  let sources: Array<Source>;
  if (clearAllWells) {
    sources = plateMap.wells.flatMap((well) => well.sources);
  } else {
    sources = [...selectedWellIndices].flatMap((index) => plateMap.wells[index].sources);
  }
  const dedupedSources = objectFromEntries(sources.map((reagent) => [reagent.id, reagent]));

  // Iterate over all the reagents and count how often we see each set of distinct schema id(s).
  const schemaGroupCounts: {[key: string]: number} = {};
  const schemasIdsByKey: {[key: string]: Array<string>} = {};
  for (const source of Object.values(dedupedSources)) {
    const schemaIds = getGroupIdsForSource(source);
    const sortedSchemaIds = naturalSort([...schemaIds]);
    const schemaKey = sortedSchemaIds.join('-');
    schemaGroupCounts[schemaKey] ??= 0;
    schemaGroupCounts[schemaKey]++;
    schemasIdsByKey[schemaKey] = sortedSchemaIds;
  }
  const schemaKeys = Object.keys(schemasIdsByKey);
  const buttonText = clearAllWells
    ? 'Clear all wells'
    : `Clear ${pluralize(selectedWellIndices.size, 'well')}`;

  return (
    <Dropdown
      className={css(styles.clearWellsDropdown)}
      closeOnInsideClick={schemaKeys.length === 1}
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.XXLARGE}>
          <Section>
            {schemaKeys.map((schemaKey) => (
              <Option
                value={schemaKey}
                key={schemaKey}
                onSelect={() => {
                  const reagentGroupIds = new Set(schemasIdsByKey[schemaKey]);
                  removeReagentGroups(reagentGroupIds, clearAllWells);
                }}
                shouldEllipsize={true}
              >
                <ClearWellsDropdownContent
                  schemas={schemasIdsByKey[schemaKey].map((schemaId) => reagentGroupsById[schemaId])}
                  numSources={schemaGroupCounts[schemaKey]}
                />
              </Option>
            ))}
          </Section>
          <Section>
            <Option
              value={CLEAR_ALL_SENTINEL}
              key={CLEAR_ALL_SENTINEL}
              onSelect={() => {
                removeAllReagentGroups(clearAllWells);
                setIsOpen(false);
              }}
            >
              <div className={css(styles.clearAll)}>Remove all sources</div>
            </Option>
          </Section>
        </Menu>
      }
      onToggle={(isOpen: boolean) => {
        if (
          // Always allow the dropdown to be closed...
          !isOpen ||
          // ...but don't allow it to be opened if there's no reagents to show
          schemaKeys.length !== 0
        ) {
          setIsOpen(isOpen);
        }
      }}
      isOpen={isOpen}
    >
      <Button
        disabled={schemaKeys.length === 0}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        icon="xmark"
        text={buttonText}
        isDropdown={true}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  clearWellsDropdown: {
    width: '120px',
  },
  clearReagentOption: {
    display: 'flex',
    alignItems: 'stretch',
  },
  swatch: {
    width: '4px',
    marginRight: '2px',
    flex: '0 0 4px',
  },
  schemasWrapper: {
    marginLeft: '6px',
    minWidth: '0px',
  },
  schemaNamesWrapper: {
    display: 'flex',
    minWidth: '0px',
  },
  schemaNames: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  seeMore: {
    color: BLUE_LIGHT,
    cursor: 'pointer',
  },
  sourcesCount: {
    color: GRAY_DARK,
  },
  clearAll: {
    color: RED_LIGHT,
  },
});
